import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Input,
  Label,
  Container,
  Spinner,
  FormGroup,
  CustomInput,
} from "reactstrap";
import { FormCheck, FormNumber, FormSelect } from "../Controls";
import LaddaButton, { SLIDE_LEFT } from "react-ladda";
import style from "./styles.module.scss";
import { CheckFormField, FormField } from "../../..";
import { toDateLocal } from "../../../utils/Helper";
import { NumberFormatInput } from "../../NumberFormatInput/NumberFormatInput";

const initialFields = {
  AssetQuoteDate: new Date(),
  QuoteCurrencyId: "",
  AssetQuotePrice: "",
  AssetQuoteAccountingPrice: "",
  AssetQuoteAdjustedPrice: "",
  AssetQuoteOpenPrice: "",
  AssetQuoteClosePrice: "",
  AssetQuotePriceIsDirty: true,
};

const ModalQuote = ({ open, toggle, quote, currencies,quotesDirty, onConfirm, decimalScale }) => {
  const [fields, setFields] = useState(initialFields);
  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (quote) {
      setFields(quote);
    } else {
      setFields({ ...initialFields, AssetQuotePriceIsDirty: quotesDirty });

    }
  }, [quote]);

  const onChange = (name, value) => {
    if (name == "AssetQuotePrice") {
      setFields({
        ...fields,
        [name]: value,
        AssetQuoteAccountingPrice: value,
        AssetQuoteOpenPrice: value,
        AssetQuoteClosePrice: value,
        AssetQuoteAdjustedPrice: value,
      });
    } else {
      setFields({ ...fields, [name]: value });
    }
    setErrors({ ...errors, [name]: undefined });
  };

  const handleSave = async () => {
    const errors = validate(fields);
    setErrors(errors);

    if (Object.keys(errors).length == 0) {
      setLoading(true);
      const { currencyISO, currencyDescription } = currencies.find(
        (e) => e.currencyId == fields.QuoteCurrencyId
      );
      const result = await onConfirm({
        ...fields,
        QuoteCurrencyISO: currencyISO,
        QuoteCurrencyDescription: currencyDescription,
        AssetQuoteDate: new Date(parseDate(fields.AssetQuoteDate)).toDateLocal(),
      });
      setLoading(false);

      if (result == 200) {
        toggle();
        setFields(initialFields);
      }
    }
  };

  const labelStyle = {
    fontWeight: "500",
    marginBottom: "5px",
    color: "#455a64 !important",
    fontSize: "13.5px !important",
  };

  return (
    <Modal isOpen={open} toggle={toggle} className={style.modal}>
      <ModalHeader toggle={toggle}>{quote ? "Edit Quote" : "Add Quote"}</ModalHeader>
      <ModalBody>
        <Container className={style.container}>
          <Row>
            <Col lg="6">
              <FormField
                name="AssetQuoteDate"
                label="Quote date"
                type="date"
                value={parseDate(fields.AssetQuoteDate)}
                onChange={onChange}
                disabled={!!quote}
              />
            </Col>
            <Col lg="6">
              {currencies ? (
                <FormSelect
                  name="QuoteCurrencyId"
                  label="Quote currency"
                  selectItems={currencies}
                  getSelectItemId={(i) => i.currencyId}
                  getSelectItemName={(i) => `${i.currencyISO} - ${i.currencyDescription}`}
                  value={fields.QuoteCurrencyId}
                  error={errors.QuoteCurrencyId}
                  disabled={false}
                  onChange={onChange}
                />
              ) : (
                <Spinner size="sm" />
              )}
            </Col>
          </Row>

          <Row>
            <Col lg="6">
              <Label style={labelStyle}>Quote price</Label>
              <NumberFormatInput
                type="text"
                customInput={Input}
                name="AssetQuotePrice"
                value={fields.AssetQuotePrice}
                onValueChange={(e) =>
                  onChange("AssetQuotePrice", isNaN(e.floatValue) ? e.value : e.floatValue)
                }
                error={errors.AssetQuotePrice}
                decimalScale={decimalScale}
                dataTypeDecimalName="price"
              />
            </Col>
            <Col lg="6">
              <Label style={labelStyle}>Accounting price</Label>
              <NumberFormatInput
                type="text"
                customInput={Input}
                name="AssetQuoteAccountingPrice"
                value={fields.AssetQuoteAccountingPrice}
                onValueChange={(e) =>
                  onChange("AssetQuoteAccountingPrice", isNaN(e.floatValue) ? e.value : e.floatValue)
                }
                error={errors.AssetQuoteAccountingPrice}
                decimalScale={decimalScale}
                dataTypeDecimalName="price"
              />
            </Col>
          </Row>

          <Row>
            <Col lg="6">
              <Label style={labelStyle}>Open price</Label>
              <NumberFormatInput
                type="text"
                customInput={Input}
                name="AssetQuoteOpenPrice"
                value={fields.AssetQuoteOpenPrice}
                onValueChange={(e) =>
                  onChange("AssetQuoteOpenPrice", isNaN(e.floatValue) ? e.value : e.floatValue)
                }
                error={errors.AssetQuoteAccountingPrice}
                decimalScale={decimalScale}
                dataTypeDecimalName="price"
              />
            </Col>
            <Col lg="6">
              <Label style={labelStyle}>Close price</Label>
              <NumberFormatInput
                type="text"
                customInput={Input}
                name="AssetQuoteClosePrice"
                value={fields.AssetQuoteClosePrice}
                onValueChange={(e) =>
                  onChange("AssetQuoteClosePrice", isNaN(e.floatValue) ? e.value : e.floatValue)
                }
                error={errors.AssetQuoteClosePrice}
                decimalScale={decimalScale}
                dataTypeDecimalName="price"
              />
            </Col>
          </Row>
          <Row>
            <Col lg="6">
              <Label style={labelStyle}>Quote XIRR</Label>
              <NumberFormatInput
                type="text"
                customInput={Input}
                name="AssetQuoteXIRR"
                value={fields.AssetQuoteXIRR}
                onValueChange={(e) =>
                  onChange("AssetQuoteXIRR", isNaN(e.floatValue) ? e.value : e.floatValue)
                }
                error={errors.AssetQuoteXIRR}
                dataTypeDecimalName="percentage"
              />
            </Col>
            <Col lg="6">
              <Label style={labelStyle}>Quote duration</Label>
              <NumberFormatInput
                type="text"
                customInput={Input}
                name="AssetQuoteDuration"
                value={fields.AssetQuoteDuration}
                onValueChange={(e) =>
                  onChange("AssetQuoteDuration", isNaN(e.floatValue) ? e.value : e.floatValue)
                }
                error={errors.AssetQuoteDuration}
                dataTypeDecimalName="currency"
              />
            </Col>

            <Col lg="6">
              <Label style={labelStyle}>Adjusted price</Label>
              <NumberFormatInput
                type="text"
                customInput={Input}
                name="AssetQuoteAdjustedPrice"
                value={fields.AssetQuoteAdjustedPrice}
                onValueChange={(e) =>
                  onChange("AssetQuoteAdjustedPrice", isNaN(e.floatValue) ? e.value : e.floatValue)
                }
                error={errors.AssetQuoteAdjustedPrice}
                decimalScale={decimalScale}
                dataTypeDecimalName="price"
              />
            </Col>
            <Col className={style.checkbox}>
              <div style={{marginTop:'5%'}}>
                <FormGroup className="mt-4">
                  <FormCheck
                    name="AssetQuoteIsDirty"
                    label="Quotes Dirty"
                    value={fields.AssetQuotePriceIsDirty}
                    onChange={(e) => onChange("AssetQuotePriceIsDirty", e.target.checked)}
                   
                  />
                </FormGroup>
                </div>
            </Col>
          </Row>
        </Container>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle}>
          Cancel
        </Button>
        <LaddaButton
          className="btn btn-ladda btn-primary pl-3 pr-3"
          loading={loading}
          disabled={false}
          onClick={() => handleSave()}
          data-style={SLIDE_LEFT}
        >
          <i className="fa fa-floppy-o mr-1" aria-hidden="true"></i>
          Save
        </LaddaButton>
      </ModalFooter>
    </Modal>
  );
};

export function parseDate(date) {
  const dateRegex = /\b\w{3} \d{2} \d{4}\b/;
  const matchedDate = String(date).match(dateRegex);
  date = matchedDate ? matchedDate[0] : date;
  if (date instanceof Date) {
    return date.toISOString();
  }

  if (date.indexOf("T") > -1) {
    return date;
  }

  if (!date || date == "" || date == "0000-00-00") {
    return "";
  }
  try {
    return new Date(`${date}T00:00`).toISOString();
  } catch (e) {
    return date;
  }
}

function validate(fields) {
  const errors = {};

  Object.entries(fields).forEach(([name, value]) => {
    if (
      name !== "QuoteCurrencyISO" &&
      name !== "QuoteCurrencyDescription" &&
      (value === undefined || value === "")
    ) {
      errors[name] = "Required field";
    }
    if (name === "QuoteCurrencyId" && value === "0") errors[name] = "Required field";
  });

  return errors;
}

export default ModalQuote;
